import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import NetflixTitle from './NetflixTitle';
import ProfilePage from './profilePage/profilePage';
import Browse from './browse/browse';
import WorkExperience from './pages/WorkExperience';
import Recommendations from './pages/Recommendations';
import Skills from './pages/Skills';
import Projects from './pages/Projects';
import ContactMe from './pages/ContactMe';
import Layout from './Layout';
import Music from './pages/Music';
import Reading from './pages/Reading';
import Blogs from './pages/Blogs';
import Certifications from './pages/Certifications';
import PageTransition from './components/PageTransition';
import ThemeToggle from './components/ThemeToggle';
import { useSelector, useDispatch } from 'react-redux';
import { setTheme } from './store/themeSlice';
import Exploration from './pages/Exploration';
import Fitness from './pages/Fitness';
import CommunityImpact from './pages/CommunityImpact';
import ResearchPublications from './pages/ResearchPublications';
import ActivityVisualization from './pages/ActivityVisualization';
import Resume from './pages/Resume';
import { trackPageView, trackScrollDepth, trackTimeOnPage } from './utils/analytics';
import './App.css';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // Safe access using optional chaining and default value
  const isDarkMode = useSelector((state: any) => state?.theme?.isDarkMode ?? true);

  useEffect(() => {
    // Apply dark mode class to body
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Check system preference
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      dispatch(setTheme(e.matches));
    };

    darkModeMediaQuery.addEventListener('change', handleChange);
    return () => darkModeMediaQuery.removeEventListener('change', handleChange);
  }, [isDarkMode, dispatch]);

  // Track page views
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);

  // Track scroll depth
  useEffect(() => {
    let maxScroll = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        if (maxScroll % 25 === 0) { // Track at 25%, 50%, 75%, 100%
          trackScrollDepth(maxScroll);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track time on page
  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      if (timeSpent % 30 === 0) { // Track every 30 seconds
        trackTimeOnPage(timeSpent);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-netflix-black text-netflix-white' : 'bg-white text-gray-900'}`}>
      <ThemeToggle />
      <Routes location={location}>
        <Route path="/" element={
          <PageTransition key="netflix-title">
            <NetflixTitle />
          </PageTransition>
        } />
        <Route path="/browse" element={
          <PageTransition key="browse">
            <Browse />
          </PageTransition>
        } />
        <Route path="/profile/:profileName" element={
          <Layout>
            <PageTransition key="profile">
              <ProfilePage />
            </PageTransition>
          </Layout>
        } />
        <Route path="/work-experience" element={
          <Layout>
            <PageTransition key="work-experience">
              <WorkExperience />
            </PageTransition>
          </Layout>
        } />
        <Route path="/recommendations" element={
          <Layout>
            <PageTransition key="recommendations">
              <Recommendations />
            </PageTransition>
          </Layout>
        } />
        <Route path="/skills" element={
          <Layout>
            <PageTransition key="skills">
              <Skills />
            </PageTransition>
          </Layout>
        } />
        <Route path="/projects" element={
          <Layout>
            <PageTransition key="projects">
              <Projects />
            </PageTransition>
          </Layout>
        } />
        <Route path="/contact-me" element={
          <Layout>
            <PageTransition key="contact-me">
              <ContactMe />
            </PageTransition>
          </Layout>
        } />
        <Route path="/music" element={
          <Layout>
            <PageTransition key="music">
              <Music />
            </PageTransition>
          </Layout>
        } />
        <Route path="/reading" element={
          <Layout>
            <PageTransition key="reading">
              <Reading />
            </PageTransition>
          </Layout>
        } />
        <Route path="/blogs" element={
          <Layout>
            <PageTransition key="blogs">
              <Blogs />
            </PageTransition>
          </Layout>
        } />
        <Route path="/certifications" element={
          <Layout>
            <PageTransition key="certifications">
              <Certifications />
            </PageTransition>
          </Layout>
        } />
        <Route path="/exploration" element={
          <Layout>
            <PageTransition key="exploration">
              <Exploration />
            </PageTransition>
          </Layout>
        } />
        <Route path="/fitness" element={
          <Layout>
            <PageTransition key="fitness">
              <Fitness />
            </PageTransition>
          </Layout>
        } />
        <Route path="/community" element={
          <Layout>
            <PageTransition key="community-impact">
              <CommunityImpact />
            </PageTransition>
          </Layout>
        } />
        <Route path="/research-publications" element={
          <Layout>
            <PageTransition key="research-publications">
              <ResearchPublications />
            </PageTransition>
          </Layout>
        } />
        <Route path="/activity-visualization" element={
          <PageTransition>
            <Layout>
              <ActivityVisualization />
            </Layout>
          </PageTransition>
        } />
        <Route path="/resume" element={
          <Layout>
            <PageTransition key="resume">
              <Resume />
            </PageTransition>
          </Layout>
        } />
      </Routes>
    </div>
  );
};

export default App;
